/**
 * Playlist feature constants — transitions, speeds, scale options, limits, and colors.
 */
import {
  SCALE_TYPES,
  FILE_TYPES,
  PLAYLIST_SPECIAL_APP_TYPES,
} from '@scio3/shared-types';

export const TRANSITION_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'fade', label: 'Fade' },
  { value: 'slideFromRight', label: 'Slide from Right' },
  { value: 'slideFromLeft', label: 'Slide from Left' },
  { value: 'rotateIn', label: 'Rotate In' },
  { value: 'flip', label: 'Flip' },
  { value: 'gallery', label: 'Gallery' },
] as const;

export type TransitionType = (typeof TRANSITION_TYPES)[number]['value'];

export const TRANSITION_SPEEDS = [
  { value: 'slow', label: 'Slow' },
  { value: 'medium', label: 'Medium' },
  { value: 'fast', label: 'Fast' },
  { value: 'custom', label: 'Custom' },
] as const;

export type TransitionSpeed = (typeof TRANSITION_SPEEDS)[number]['value'];

export const SCALE_OPTIONS = [
  { value: SCALE_TYPES.NONE, label: 'None' },
  { value: SCALE_TYPES.FIT, label: 'Fit' },
  { value: SCALE_TYPES.STRETCH, label: 'Stretch' },
  { value: SCALE_TYPES.ZOOM, label: 'Zoom' },
] as const;

export const ORIENTATION_OPTIONS = [
  { value: 'landscape', label: 'Landscape' },
  { value: 'portrait', label: 'Portrait' },
  { value: 'both', label: 'Both' },
] as const;

export const PLAYLIST_COLORS = [
  '#1C84C6',
  '#81D4FA',
  '#1AB394',
  '#ED5565',
  '#F8AC59',
  '#795548',
] as const;

export const PLAY_EVERY_OPTIONS = [
  { value: 15, label: 'Every 15 min' },
  { value: 30, label: 'Every 30 min' },
  { value: 60, label: 'Every 1 hour' },
  { value: 180, label: 'Every 3 hours' },
  { value: 360, label: 'Every 6 hours' },
  { value: 720, label: 'Every 12 hours' },
  { value: 1440, label: 'Every 24 hours' },
] as const;

/** Apply-to values for content targeting (adv. playback settings).
 * Values are GraphQL enum keys from PLAYLIST_MESSAGE_APPLY_TO (backend maps PLAY_ON→'in', NOT_PLAY_ON→'nin'). */
export const PLAYLIST_MESSAGE_APPLY_TO = {
  ALL: 'null',
  PLAY_ON: 'PLAY_ON',
  NOT_PLAY_ON: 'NOT_PLAY_ON',
} as const;

/** Device type options for content targeting.
 * Values are GraphQL enum keys from PLAYLIST_APPLY_TO_DATA_TYPE (backend maps DEVICE→'device', TAG→'tag', ALL_SCREEN→'all'). */
export const PLAYLIST_MESSAGE_DEVICE_TYPE = {
  DEVICE: 'DEVICE',
  TAG: 'TAG',
  ALL_SCREEN: 'ALL_SCREEN',
} as const;

/** Default custom play-every value in minutes (when user picks "Custom") */
export const DEFAULT_CUSTOM_PLAY_EVERY_VALUE = 120;

/** Normalize apply-to value from backend — may arrive as lowercase DB value ('in'/'nin') or UPPERCASE GraphQL enum key. */
export function normalizeApplyToValue(value: string): string {
  const map: Record<string, string> = {
    in: PLAYLIST_MESSAGE_APPLY_TO.PLAY_ON,
    nin: PLAYLIST_MESSAGE_APPLY_TO.NOT_PLAY_ON,
  };
  return map[value] ?? value;
}

/** Normalize device type value from backend — may arrive as lowercase DB value ('device'/'tag'/'all') or UPPERCASE GraphQL enum key. */
export function normalizeDeviceTypeValue(value: string): string {
  const map: Record<string, string> = {
    device: PLAYLIST_MESSAGE_DEVICE_TYPE.DEVICE,
    tag: PLAYLIST_MESSAGE_DEVICE_TYPE.TAG,
    all: PLAYLIST_MESSAGE_DEVICE_TYPE.ALL_SCREEN,
  };
  return map[value] ?? value;
}

/** Resume on next play options for item options */
export const RESUME_ON_NEXT_PLAY_OPTIONS = [
  { value: 'usePlDefault', label: 'Use Playlist Default' },
  { value: 'true', label: 'ON' },
  { value: 'false', label: 'OFF' },
] as const;

/** Target tags mode options */
export const TARGET_TAGS_OPTIONS = [
  { value: 'usePlDefault', label: 'Use Playlist Default' },
  { value: 'custom', label: 'Custom' },
] as const;

/** Target tags mode options for playlist-parent type */
export const TARGET_TAGS_PLAYLIST_OPTIONS = [
  { value: 'usePlDefault', label: 'Use Playlist Default' },
  { value: 'useAsset', label: 'Use Asset Default' },
] as const;

/** Apply-to options for adv. playback settings.
 * '__all__' is the sentinel for no apply-to filter (maps to empty string in applyToData). */
export const APPLY_TO_OPTIONS = [
  { value: '__all__', label: 'All' },
  { value: PLAYLIST_MESSAGE_APPLY_TO.PLAY_ON, label: 'Play on' },
  { value: PLAYLIST_MESSAGE_APPLY_TO.NOT_PLAY_ON, label: 'Not play on' },
] as const;

/** Target screen type options (Screens or Tags — required when applyTo is set). */
export const TARGET_SCREEN_TYPE_OPTIONS = [
  { value: PLAYLIST_MESSAGE_DEVICE_TYPE.DEVICE, label: 'Screens' },
  { value: PLAYLIST_MESSAGE_DEVICE_TYPE.TAG, label: 'Tags' },
] as const;

/**
 * Map frontend camelCase targetTagsDefault values → GraphQL UPPERCASE enum values.
 * Backend enum: PLAYLIST_TARGET_TAGS { USE_PLAYLIST_DEFAULT = "usePlDefault", USE_ASSET = "useAsset" }
 * Read path normalizes UPPERCASE → camelCase (PlaylistOptionsDialog buildInitialSubPlaylistData).
 * Write path must reverse-map back to UPPERCASE for mutations.
 */
export const TARGET_TAGS_DEFAULT_TO_GQL: Record<string, string> = {
  usePlDefault: 'USE_PLAYLIST_DEFAULT',
  useAsset: 'USE_ASSET',
  custom: 'CUSTOM',
};

/**
 * App types that behave like sub-playlists (no transition/speed, has sub-playlist control).
 * Matches scio 2.0's ASSET_APP_TYPE_PLAYLIST_SPECIAL from @optisigns/common-constant.
 *
 * Single source of truth: `PLAYLIST_SPECIAL_APP_TYPES` in @scio3/shared-types. This is a
 * local alias kept for the playlist-feature naming convention — do NOT redefine the list
 * here, or the inline lock (isDurationEditLocked) and bulk lock (EditDurationDialog) drift.
 */
export const ASSET_APP_TYPE_PLAYLIST_SPECIAL = PLAYLIST_SPECIAL_APP_TYPES;

/**
 * Whether an appType behaves like a sub-playlist (MRSS, Google Drive, Dropbox, OneDrive,
 * contentRotation) — i.e. its content is a backing playlist, not a renderable single asset.
 * Shared membership test so preview / push-to-screens / duration-lock stay in lockstep.
 */
export function isPlaylistSpecialAppType(appType?: string | null): boolean {
  return !!appType && (ASSET_APP_TYPE_PLAYLIST_SPECIAL as readonly string[]).includes(appType);
}

/** Asset app types that allow resumeOnNextPlay */
export const RESUME_ON_NEXT_PLAY_ASSET_FILE_TYPES = [
  FILE_TYPES.VIDEO,
  FILE_TYPES.DOCUMENT,
] as const;
export const RESUME_ON_NEXT_PLAY_ASSET_WEB_TYPES = ['youtube', 'vimeo'] as const;
export const RESUME_ON_NEXT_PLAY_ASSET_TYPES = ['splitScreen'] as const;

/** Playlist options defaults — matches scio1 initialization values */
export const DEFAULT_SCALE = SCALE_TYPES.FIT;
export const DEFAULT_TRANSITION = 'fade';
export const DEFAULT_SPEED = 'medium';
export const DEFAULT_SPEED_VALUE = 1.0;
export const DEFAULT_ORIENTATION = 'NONE';
export const DEFAULT_BACKGROUND_AUDIO_TYPE = 'none';
/** normalDuration for fade transition (ms) — scio1: 1500 */
export const NORMAL_DURATION_FADE = 1500;
/** normalDuration for non-fade transitions (ms) — scio1: 2000 */
export const NORMAL_DURATION_DEFAULT = 2000;
/** Speed slider range */
export const SPEED_SLIDER_MIN = 0.1;
export const SPEED_SLIDER_MAX = 2.0;
export const SPEED_SLIDER_STEP = 0.1;

export const MIN_DURATION = 4;
export const MAX_DURATION = 9999;
export const MAX_DURATION_SUB_PLAYLIST = 86400;
export const DEFAULT_DURATION = 20;

/*
 * `shouldIgnoreAppStoredDuration()` lived here until #1967.
 *
 * It replaced an app asset's stored duration with the account default on add (#1802 req 4).
 * Product (19 Aug) locked the resolution order as
 *   Asset duration > appMeta > account-level default > global default (20)
 * so the asset's own duration now wins and there is nothing left for the helper to decide:
 * `resolvePlaylistItemDuration` already implements that order, including the one case that still
 * ignores a stored value (a page-count document, whose duration is an upload-time stamp of the
 * account default rather than a real length).
 *
 * Removed rather than left returning false, so nobody wires it back in. See open-api
 * `playlist-duration.ts` for the same change on the persisting side.
 */

/**
 * Whether a playlist item's duration is locked (read-only) in the editor.
 * Mirrors scio1's duration-input disable conditions (editSection.html).
 *
 * Locked when EITHER:
 *  1. Special app type (MRSS, cloud drives, Content Rotation) — backed by a
 *     system-managed hidden playlist whose totalDuration is recomputed by
 *     background jobs (e.g. the MRSS feed job's `updatePlaylistParent()`
 *     overwrites the parent-item `duration` with the feed total on every fetch,
 *     so a user-set value silently reverts — the reported bug). scio1 disables
 *     this via `ASSET_APPTYPE_PLAYLIST_SPECIAL.includes(appType)`.
 *  2. `duration === 0` — scio1 disables the input when `asset.duration == 0`
 *     (applies to BOTH non-playlist and playlist-type inputs). New items added
 *     in scio3 are clamped to >= MIN_DURATION, but legacy items loaded from the
 *     DB can carry duration 0. Only an exact 0 locks; undefined/null does not
 *     (those fall back to DEFAULT_DURATION and stay editable).
 *
 * NOT applied to real nested playlists with a positive duration (`type ===
 * 'playlist'`, no special appType): they stay user-editable when
 * subPlaylistControl is off, matching scio1's playlist-type input.
 */
export function isDurationEditLocked(
  appType: string | undefined | null,
  duration?: number | null,
): boolean {
  if (duration === 0) return true;
  return (
    !!appType &&
    ASSET_APP_TYPE_PLAYLIST_SPECIAL.includes(
      appType as (typeof ASSET_APP_TYPE_PLAYLIST_SPECIAL)[number],
    )
  );
}

/** CDN URL for nested playlist thumbnails */
export const PLAYLIST_THUMBNAIL_URL = `${import.meta.env.VITE_CDN_URL || 'https://cdn.optisigns.com'}/scio/images/playlist.png`;
export const AUTO_SAVE_DEBOUNCE = 3000;
export const MAX_HISTORY_DEPTH = 20;
/** Bulk remove threshold: below this count, use debounced auto-save; at or above, call removePlaylistItems API directly. */
export const BULK_REMOVE_API_THRESHOLD = 50;
export const SEARCH_DEBOUNCE = 300;
export const DEFAULT_PAGE_SIZE = 50;
/** Fetch all items — matches scio2 PAGE_SETTING.LOAD_ALL. Used when resolving folder contents. */
export const LOAD_ALL_LIMIT = 99999;
